/**
 * Lying snow you can see the depth of, wade through and leave tracks in.
 *
 * The terrain has a vertex every five metres, far too coarse for a
 * footprint, so the snow is a layer of its own around the player: a grid,
 * dense under the player (7 cm between vertices) and coarser out to its
 * edge, draped over the ground on the GPU and only as thick as the snow is
 * there. It fades into the terrain's own white tint at its edge.
 *
 * Three textures say how thick, each kept around the player and drawn on
 * the GPU, so no frame waits on them:
 *
 * - **The ground's height**, from the terrain drawn from straight above
 *   (`GROUND_LAYER`), every couple of seconds as tiles come and go.
 * - **What is cleared**: roads, paths and water drawn the same way
 *   (`CLEARED_LAYER`). Plowed, with the snow thrown up in a bank along
 *   their edges.
 * - **What is pressed down**: every footprint and wheel track, stamped in
 *   as it happens with max blending, moved along with the player, and
 *   filled in again by new snow.
 *
 * A coarse copy of the tracks is kept on the CPU for the physics: driving in
 * your own ruts is easier than through fresh snow.
 */

import type { Scene, Texture, WebGLRenderer } from 'three';
import {
  BufferAttribute, BufferGeometry, Color, CustomBlending, DoubleSide, DynamicDrawUsage, FloatType, Group, HalfFloatType, InstancedBufferAttribute,
  InstancedMesh, LinearFilter, Matrix4, MaxEquation, Mesh, MeshBasicMaterial, MeshStandardMaterial, NoBlending, OneFactor,
  OrthographicCamera, PlaneGeometry, Quaternion, RGBAFormat, ReverseSubtractEquation, Scene as ThreeScene, ShaderMaterial,
  UnsignedByteType, Vector2, Vector3, WebGLRenderTarget,
} from 'three';
import type { WebGLProgramParametersWithUniforms } from 'three';
import { DUSTING, FILLS_IN_CM, PACKED, PLOWED_LANDCOVER } from '../core/weather/snow.js';
import { GLSL_LAMP_LIGHT, floraNight, lampLight } from './terrainMaterial.js';
import { CLEARED_LAYER, GROUND_LAYER, SURFACE_LAYER } from './layers.js';
import type { TerrainLandcover } from './terrainMaterial.js';


/** The ground's height and the cleared mask: metres across, and texels. */
const GROUND_SIZE = 128;
const GROUND_RES = 512;
/** The tracks: metres across, and texels (10 cm each). */
const TRACK_SIZE = 102.4;
const TRACK_RES = 1024;
/** The CPU's copy of the tracks, for the physics: texels across (40 cm each). */
const MIRROR_RES = 256;
/**
 * The snow's two grids: a fine one under the player, a coarse one out to
 * the edge with a hole the fine one fills. Each moves in steps of its own
 * spacing, so its vertices stay put on the ground as the player moves; a
 * grid that slid under them would sample the snow somewhere new each frame,
 * and its thin edges would flicker.
 */
const INNER = { radius: 12.3, step: 0.15 };
const OUTER = { radius: 48, step: 0.75, hole: 12 };
/** How far the snow reaches, for its fade at the edge. */
const MESH_RADIUS = OUTER.radius;
/** Its surface over the ground, and the thinnest drawn, metres: clear of the terrain's depth. */
const LIFT = 0.02;

/** How far the player goes from a texture's middle before it moves to them, metres. */
const GROUND_SLACK = 16;
const TRACK_SLACK = 22;
/** Stamps drawn into the tracks a frame, at most. */
const MAX_STAMPS = 256;

/** Texture coordinates of scene (x, z) in a texture `size` across around `centre`, as the cameras from above draw it. */
const GLSL_SNOW = /* glsl */ `
uniform sampler2D uSnowGround;
uniform sampler2D uSnowCleared;
uniform sampler2D uSnowTracks;
uniform vec2 uGroundCentre;
uniform vec2 uTrackCentre;
uniform vec2 uMeshCentre;
uniform vec2 uFadeCentre;
uniform vec2 uHoleCentre;
uniform float uHole;
uniform float uGroundRef;
uniform float uSnowDepth;
vec2 snowUv(vec2 p, vec2 centre, float size) {
  return vec2((p.x - centre.x) / size + 0.5, 0.5 - (p.y - centre.y) / size);
}
bool snowInside(vec2 uv) { return uv.x > 0.002 && uv.x < 0.998 && uv.y > 0.002 && uv.y < 0.998; }
float snowHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float snowNoise(vec2 p) {
  vec2 i = floor(p), f = fract(p), w = f * f * (3.0 - 2.0 * f);
  return mix(mix(snowHash(i), snowHash(i + vec2(1, 0)), w.x), mix(snowHash(i + vec2(0, 1)), snowHash(i + vec2(1, 1)), w.x), w.y);
}
float snowPressed(vec2 p) {
  vec2 uv = snowUv(p, uTrackCentre, ${TRACK_SIZE.toFixed(1)});
  return snowInside(uv) ? texture2D(uSnowTracks, uv).r : 0.0;
}
/** Cleared, blurred over a metre: the plowed edge's slope; and the bank along it. */
vec2 snowCleared(vec2 p) {
  vec2 uv = snowUv(p, uGroundCentre, ${GROUND_SIZE.toFixed(1)});
  float t = 1.0 / ${GROUND_SIZE.toFixed(1)};
  float near = 0.0, wide = 0.0;
  for (int i = 0; i < 4; i++) {
    vec2 o = vec2(i == 0 ? 1.0 : i == 1 ? -1.0 : 0.0, i == 2 ? 1.0 : i == 3 ? -1.0 : 0.0);
    near += texture2D(uSnowCleared, uv + o * t * 0.6).r;
    wide += texture2D(uSnowCleared, uv + o * t * 2.2).r;
  }
  near = max(near * 0.25, texture2D(uSnowCleared, uv).r);
  wide *= 0.25;
  // Off the road but near it: the snow the plough threw there.
  float bank = clamp(wide - near, 0.0, 1.0) * (1.0 - near);
  return vec2(near, bank);
}
/** The snow's thickness at scene (x, z), metres, and the ground's height under it (in .y, NaN-free: 0 with no ground). */
vec3 snowAt(vec2 wxz) {
  vec2 guv = snowUv(wxz, uGroundCentre, ${GROUND_SIZE.toFixed(1)});
  vec4 g = snowInside(guv) ? texture2D(uSnowGround, guv) : vec4(0.0);
  float pressed = snowPressed(wxz);
  vec2 cl = snowCleared(wxz);
  // Drifts: the depth varies a little across the ground.
  float drift = 0.85 + 0.3 * snowNoise(wxz * 0.21);
  float thick = uSnowDepth * drift * (1.0 - cl.x) * (1.0 + 0.7 * cl.y) * (1.0 - ${(1 - PACKED).toFixed(2)} * pressed);
  // Out at the edge, thinning into the terrain's own white.
  vec2 off = abs(wxz - uFadeCentre);
  thick *= 1.0 - smoothstep(0.72, 0.98, max(off.x, off.y) / ${MESH_RADIUS.toFixed(1)});
  if (g.g < 0.5) thick = 0.0;
  return vec3(thick, g.r, pressed);
}
`;

export class SnowField {
  readonly mesh = new Group();
  /** Each grid's middle, set in its own steps. */
  private readonly levels: Array<{ centre: Vector2; step: number }> = [];
  private readonly uniforms = {
    uSnowGround: { value: null as Texture | null },
    uSnowCleared: { value: null as Texture | null },
    uSnowTracks: { value: null as Texture | null },
    uGroundCentre: { value: new Vector2() },
    uTrackCentre: { value: new Vector2() },
    uFadeCentre: { value: new Vector2() },
    uHoleCentre: { value: new Vector2() },
    uGroundRef: { value: 0 },
    uSnowDepth: { value: 0 },
  };
  private readonly ground: WebGLRenderTarget;
  private readonly cleared: WebGLRenderTarget;
  private tracks: WebGLRenderTarget;
  private spare: WebGLRenderTarget;
  private readonly above = new OrthographicCamera(-GROUND_SIZE / 2, GROUND_SIZE / 2, GROUND_SIZE / 2, -GROUND_SIZE / 2, 1, 6000);
  private readonly trackCamera = new OrthographicCamera(-TRACK_SIZE / 2, TRACK_SIZE / 2, TRACK_SIZE / 2, -TRACK_SIZE / 2, 0.1, 100);
  private readonly heightMaterial: ShaderMaterial;
  private readonly clearedMaterial = new MeshBasicMaterial({ color: 0xffffff, side: DoubleSide, depthTest: false, fog: false });
  /** Car parks: the surfaces that are parking bays, and the terrain painted as one, a material for each tile's raster. */
  private readonly baysMaterial = new ShaderMaterial({
    vertexShader: /* glsl */ `
      attribute vec4 aSurface;
      varying float vType;
      void main() {
        vType = aSurface.z;
        gl_Position = projectionMatrix * viewMatrix * modelMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      varying float vType;
      void main() {
        if (abs(vType - 7.0) > 0.5) discard;
        gl_FragColor = vec4(1.0);
      }`,
    side: DoubleSide, depthTest: false, depthWrite: false,
  });
  private readonly parkingMaterials = new WeakMap<Texture, ShaderMaterial>();
  private readonly stampScene = new ThreeScene();
  private readonly stamps: InstancedMesh;
  private stampCount = 0;
  private readonly quadScene = new ThreeScene();
  private readonly shiftMaterial: ShaderMaterial;
  private readonly fillMaterial: ShaderMaterial;
  private readonly quad: Mesh;
  /** The tracks on the CPU, coarse: 0..1 pressed. */
  private readonly mirror = new Float32Array(MIRROR_RES * MIRROR_RES);
  private groundCentre = new Vector2(Infinity, Infinity);
  private trackCentre = new Vector2(Infinity, Infinity);
  private groundAge = Infinity;
  private shift: Vector2 | undefined;
  private clearTracks = true;
  /** Fresh snow not yet put into the tracks, as a share of a full track. */
  private fill = 0;
  private depth = 0;

  constructor(
    private readonly renderer: WebGLRenderer,
    /** The terrain pieces wearing a landcover raster, for the car parks on it. */
    private readonly landcoverPieces: () => Iterable<{ mesh: Mesh; landcover: TerrainLandcover }> = () => [],
  ) {
    // Heights as full floats where the GPU filters them: half floats lose
    // centimetres tens of metres up or down, and thin snow sank into the ground.
    const float = renderer.extensions.has('OES_texture_float_linear');
    const target = (res: number, height: boolean) => new WebGLRenderTarget(res, res, {
      type: height ? (float ? FloatType : HalfFloatType) : UnsignedByteType, format: RGBAFormat, depthBuffer: height,
      minFilter: LinearFilter, magFilter: LinearFilter, generateMipmaps: false,
    });
    this.ground = target(GROUND_RES, true);
    this.cleared = target(GROUND_RES, false);
    this.tracks = target(TRACK_RES, false);
    this.spare = target(TRACK_RES, false);
    this.uniforms.uSnowGround.value = this.ground.texture;
    this.uniforms.uSnowCleared.value = this.cleared.texture;
    this.uniforms.uSnowTracks.value = this.tracks.texture;

    // The ground's height above a reference, and a flag that there is ground there at all.
    this.heightMaterial = new ShaderMaterial({
      uniforms: { uRef: this.uniforms.uGroundRef },
      vertexShader: /* glsl */ `
        varying float vY;
        void main() {
          vec4 world = modelMatrix * vec4(position, 1.0);
          vY = world.y;
          gl_Position = projectionMatrix * viewMatrix * world;
        }`,
      fragmentShader: /* glsl */ `
        uniform float uRef;
        varying float vY;
        void main() { gl_FragColor = vec4(vY - uRef, 1.0, 0.0, 1.0); }`,
      side: DoubleSide,
    });
    this.above.up.set(0, 0, -1);
    this.trackCamera.up.set(0, 0, -1);
    this.above.layers.set(GROUND_LAYER);

    // A footprint or a stretch of tyre track: a soft-edged ellipse or a
    // rounded strip, pressed down by `aPressure`, the deepest press kept.
    const stampGeometry = new PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
    stampGeometry.setAttribute('aStamp', new InstancedBufferAttribute(new Float32Array(MAX_STAMPS * 2), 2).setUsage(DynamicDrawUsage));
    this.stamps = new InstancedMesh(stampGeometry, new ShaderMaterial({
      vertexShader: /* glsl */ `
        attribute vec2 aStamp;
        varying vec2 vUv;
        varying vec2 vStamp;
        void main() {
          vUv = uv;
          vStamp = aStamp;
          gl_Position = projectionMatrix * viewMatrix * modelMatrix * instanceMatrix * vec4(position, 1.0);
        }`,
      fragmentShader: /* glsl */ `
        varying vec2 vUv;
        varying vec2 vStamp;
        void main() {
          vec2 p = vUv * 2.0 - 1.0;
          // 0: a footprint, an ellipse narrower at the heel; 1: a tyre's strip.
          float shape = vStamp.y < 0.5
            ? 1.0 - smoothstep(0.7, 1.0, length(vec2(p.x * (1.0 + 0.25 * step(0.0, p.y)), p.y)))
            : 1.0 - smoothstep(0.75, 1.0, abs(p.x));
          gl_FragColor = vec4(vStamp.x * shape);
        }`,
      blending: CustomBlending, blendEquation: MaxEquation, blendSrc: OneFactor, blendDst: OneFactor,
      depthTest: false, depthWrite: false, side: DoubleSide,
    }), MAX_STAMPS);
    this.stamps.frustumCulled = false;
    this.stamps.count = 0;
    this.stampScene.add(this.stamps);

    // Full-screen passes over the tracks: moved along, and filled in.
    this.shiftMaterial = new ShaderMaterial({
      uniforms: { uFrom: { value: null as Texture | null }, uOffset: { value: new Vector2() } },
      vertexShader: 'varying vec2 vUv; void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }',
      fragmentShader: /* glsl */ `
        uniform sampler2D uFrom;
        uniform vec2 uOffset;
        varying vec2 vUv;
        void main() {
          vec2 uv = vUv + uOffset;
          bool inside = uv.x >= 0.0 && uv.x <= 1.0 && uv.y >= 0.0 && uv.y <= 1.0;
          gl_FragColor = inside ? texture2D(uFrom, uv) : vec4(0.0);
        }`,
      blending: NoBlending, depthTest: false, depthWrite: false,
    });
    this.fillMaterial = new ShaderMaterial({
      uniforms: { uAmount: { value: 0 } },
      vertexShader: 'void main() { gl_Position = vec4(position.xy, 0.0, 1.0); }',
      fragmentShader: 'uniform float uAmount; void main() { gl_FragColor = vec4(uAmount); }',
      blending: CustomBlending, blendEquation: ReverseSubtractEquation, blendSrc: OneFactor, blendDst: OneFactor,
      depthTest: false, depthWrite: false,
    });
    this.quad = new Mesh(new PlaneGeometry(2, 2), this.shiftMaterial);
    this.quad.frustumCulled = false;
    this.quadScene.add(this.quad);

    for (const level of [INNER, OUTER]) {
      const centre = new Vector2();
      const mesh = new Mesh(snowGrid(level.radius, level.step), this.snowMaterial(centre, level === OUTER ? OUTER.hole : 0));
      mesh.frustumCulled = false;
      mesh.matrixAutoUpdate = false;
      mesh.receiveShadow = true;
      this.mesh.add(mesh);
      this.levels.push({ centre, step: level.step });
    }
    this.mesh.visible = false;
  }

  /** The snow's material for a grid around `centre`, with a hole `hole` metres either way for the finer grid. */
  private snowMaterial(centre: Vector2, hole: number): MeshStandardMaterial {
    const material = new MeshStandardMaterial({ color: new Color(0xffffff), roughness: 0.82, metalness: 0 });
    material.onBeforeCompile = (shader: WebGLProgramParametersWithUniforms) => {
      Object.assign(shader.uniforms, this.uniforms);
      shader.uniforms.uMeshCentre = { value: centre };
      shader.uniforms.uHole = { value: hole };
      shader.uniforms.uNight = floraNight;
      shader.uniforms.uLampMap = lampLight.map;
      shader.uniforms.uLampOrigin = lampLight.origin;
      shader.uniforms.uLampInvExtent = lampLight.invExtent;
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', `#include <common>\n${GLSL_SNOW}\nvarying float vThick;\nvarying float vPressed;\nvarying vec3 vSnowWorld;`)
        // Smooth normals from the snow's own surface, a hand's width either
        // way: soft drifts, and the walls of a rut without a sawtooth edge.
        .replace('#include <beginnormal_vertex>', /* glsl */ `
  vec2 wxz = uMeshCentre + position.xz;
  vec3 here = snowAt(wxz);
  const float E = 0.12;
  vec3 ex0 = snowAt(wxz - vec2(E, 0.0)), ex1 = snowAt(wxz + vec2(E, 0.0));
  vec3 ez0 = snowAt(wxz - vec2(0.0, E)), ez1 = snowAt(wxz + vec2(0.0, E));
  float hx = (ex1.x + ex1.y) - (ex0.x + ex0.y), hz = (ez1.x + ez1.y) - (ez0.x + ez0.y);
  vec3 objectNormal = normalize(vec3(-hx, 2.0 * E, -hz));`)
        .replace('#include <begin_vertex>', /* glsl */ `
  vThick = here.x;
  vPressed = here.z;
  vec3 transformed = vec3(wxz.x, uGroundRef + here.y + here.x + ${LIFT.toFixed(3)}, wxz.y);
  vSnowWorld = transformed;`);
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', `#include <common>\nuniform float uNight;\n${GLSL_SNOW}\nvarying float vThick;\nvarying float vPressed;\nvarying vec3 vSnowWorld;\n${GLSL_LAMP_LIGHT}`)
        .replace('#include <color_fragment>', /* glsl */ `#include <color_fragment>
  if (vThick < ${(DUSTING * 0.75).toFixed(3)}) discard;
  // The coarse grid leaves the middle to the fine one.
  vec2 fromHole = abs(vSnowWorld.xz - uHoleCentre);
  if (uHole > 0.0 && max(fromHole.x, fromHole.y) < uHole) discard;
  // Fresh snow white with a faint blue; packed snow in prints and ruts grey,
  // by the pixel, so a print is crisp on the coarse grid too.
  vec3 fresh = vec3(0.9, 0.93, 0.98);
  vec3 packed = vec3(0.5, 0.55, 0.66);
  diffuseColor.rgb *= mix(fresh, packed, smoothstep(0.1, 0.75, snowPressed(vSnowWorld.xz)));`)
        .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
  totalEmissiveRadiance += streetLampRadiance(vSnowWorld.xz, diffuseColor.rgb, uNight, 0.0);`);
    };
    return material;
  }

  /** Draws white where a tile's landcover raster says car park. */
  private parkingMaterial(landcover: TerrainLandcover): ShaderMaterial {
    let material = this.parkingMaterials.get(landcover.texture);
    if (material === undefined) {
      material = new ShaderMaterial({
        uniforms: { uLandcover: { value: landcover.texture }, uExtent: { value: new Vector2(landcover.width, landcover.depth) } },
        vertexShader: /* glsl */ `
          varying vec2 vLocal;
          void main() {
            vLocal = position.xz;
            gl_Position = projectionMatrix * viewMatrix * modelMatrix * vec4(position, 1.0);
          }`,
        fragmentShader: /* glsl */ `
          uniform sampler2D uLandcover;
          uniform vec2 uExtent;
          varying vec2 vLocal;
          void main() {
            ivec2 size = textureSize(uLandcover, 0);
            ivec2 at = clamp(ivec2(floor(vLocal / uExtent * vec2(size))), ivec2(0), size - 1);
            int cls = int(texelFetch(uLandcover, at, 0).r * 255.0 + 0.5);
            if (${PLOWED_LANDCOVER.map(c => `cls != ${c}`).join(' && ')}) discard;
            gl_FragColor = vec4(1.0);
          }`,
        side: DoubleSide, depthTest: false, depthWrite: false,
      });
      this.parkingMaterials.set(landcover.texture, material);
    }
    return material;
  }

  /** How pressed down the snow is at scene (x, z), 0..1, from the coarse copy. */
  pressedAt(x: number, z: number): number {
    const i = this.mirrorIndex(x, z);
    return i < 0 ? 0 : this.mirror[i];
  }

  private mirrorIndex(x: number, z: number): number {
    const u = Math.floor(((x - this.trackCentre.x) / TRACK_SIZE + 0.5) * MIRROR_RES);
    const v = Math.floor(((z - this.trackCentre.y) / TRACK_SIZE + 0.5) * MIRROR_RES);
    return u < 0 || v < 0 || u >= MIRROR_RES || v >= MIRROR_RES ? -1 : v * MIRROR_RES + u;
  }

  /** A footprint at scene (x, z), the foot along `yaw`. */
  footprint(x: number, z: number, yaw: number, pressure = 0.8): void {
    this.stamp(x, z, yaw, 0.12, 0.29, pressure, 0);
  }

  /** A tyre's track from (x0, z0) to (x1, z1), `width` wide. */
  track(x0: number, z0: number, x1: number, z1: number, width: number, pressure = 1): void {
    const dx = x1 - x0, dz = z1 - z0, length = Math.hypot(dx, dz);
    if (length < 0.02 || length > 8) return;
    this.stamp((x0 + x1) / 2, (z0 + z1) / 2, Math.atan2(-dx, -dz), width, length + width * 0.5, pressure, 1);
  }

  private stamp(x: number, z: number, yaw: number, width: number, length: number, pressure: number, shape: number): void {
    if (this.depth < DUSTING || this.stampCount >= MAX_STAMPS) return;
    stampMatrix.compose(stampAt.set(x, 0, z), stampTurn.setFromAxisAngle(UP, yaw), stampScale.set(width, 1, length));
    this.stamps.setMatrixAt(this.stampCount, stampMatrix);
    const a = this.stamps.geometry.getAttribute('aStamp') as InstancedBufferAttribute;
    a.setXY(this.stampCount, pressure, shape);
    this.stampCount++;
    // The coarse copy, for the physics.
    const reach = Math.max(width, length) / 2;
    for (let ox = -reach; ox <= reach; ox += 0.35) {
      for (let oz = -reach; oz <= reach; oz += 0.35) {
        const i = this.mirrorIndex(x + ox, z + oz);
        if (i >= 0) this.mirror[i] = Math.max(this.mirror[i], pressure * 0.8);
      }
    }
  }

  /**
   * Once a frame: the snow's depth now, metres, and the snowfall, cm/h;
   * where the player is (scene x, y, z). Moves the textures along with
   * them, fills tracks in with new snow, and sets the mesh.
   */
  update(dt: number, depth: number, snowfall: number, at: { x: number; y: number; z: number }): void {
    this.depth = depth;
    this.uniforms.uSnowDepth.value = depth;
    this.mesh.visible = depth >= DUSTING;
    if (!this.mesh.visible) {
      this.stampCount = 0;
      return;
    }
    // A jump (a rebase, a teleport): start the tracks again where the player is.
    if (Math.hypot(at.x - this.trackCentre.x, at.z - this.trackCentre.y) > TRACK_SIZE) {
      this.trackCentre.set(snapTo(at.x, TRACK_SIZE / TRACK_RES), snapTo(at.z, TRACK_SIZE / TRACK_RES));
      this.clearTracks = true;
      this.mirror.fill(0);
    } else if (Math.hypot(at.x - this.trackCentre.x, at.z - this.trackCentre.y) > TRACK_SLACK) {
      const next = new Vector2(snapTo(at.x, TRACK_SIZE / TRACK_RES), snapTo(at.z, TRACK_SIZE / TRACK_RES));
      this.shift = next.clone().sub(this.trackCentre);
      this.shiftMirror(this.shift);
      this.trackCentre.copy(next);
    }
    this.groundAge += dt;
    if (this.groundAge > 4 || Math.hypot(at.x - this.groundCentre.x, at.z - this.groundCentre.y) > GROUND_SLACK) {
      this.groundCentre.set(at.x, at.z);
      this.uniforms.uGroundRef.value = Math.round(at.y);
      this.groundAge = Number.NaN; // asks `render` to draw it
    }
    // Each grid in steps of its own spacing, so its vertices do not swim.
    for (const level of this.levels) level.centre.set(snapTo(at.x, level.step), snapTo(at.z, level.step));
    this.uniforms.uHoleCentre.value.copy(this.levels[0].centre);
    this.uniforms.uFadeCentre.value.set(at.x, at.z);
    // Fresh snow fills the tracks in: a full track over `FILLS_IN_CM`.
    const fresh = (snowfall / 3600) * dt / FILLS_IN_CM;
    if (fresh > 0) {
      this.fill += fresh;
      for (let i = 0; i < this.mirror.length; i++) this.mirror[i] = Math.max(0, this.mirror[i] - fresh);
    }
  }

  private shiftMirror(by: Vector2): void {
    const du = Math.round((by.x / TRACK_SIZE) * MIRROR_RES), dv = Math.round((by.y / TRACK_SIZE) * MIRROR_RES);
    const old = this.mirror.slice();
    this.mirror.fill(0);
    for (let v = 0; v < MIRROR_RES; v++) {
      const sv = v + dv;
      if (sv < 0 || sv >= MIRROR_RES) continue;
      for (let u = 0; u < MIRROR_RES; u++) {
        const su = u + du;
        if (su >= 0 && su < MIRROR_RES) this.mirror[v * MIRROR_RES + u] = old[sv * MIRROR_RES + su];
      }
    }
  }

  /** The GPU's part, before the frame is drawn: the ground from above, the tracks moved, filled and stamped. */
  render(scene: Scene): void {
    if (!this.mesh.visible) return;
    const r = this.renderer;
    const was = r.getRenderTarget();
    const autoClear = r.autoClear;
    const shadows = r.shadowMap.autoUpdate;
    const clearColor = r.getClearColor(new Color()), clearAlpha = r.getClearAlpha();
    r.shadowMap.autoUpdate = false;
    r.setClearColor(0x000000, 0);

    if (Number.isNaN(this.groundAge)) {
      this.groundAge = 0;
      const background = scene.background, fog = scene.fog, override = scene.overrideMaterial;
      scene.background = null;
      scene.fog = null;
      this.above.position.set(this.groundCentre.x, this.uniforms.uGroundRef.value + 3000, this.groundCentre.y);
      this.above.lookAt(this.groundCentre.x, 0, this.groundCentre.y);
      this.above.updateMatrixWorld();
      this.uniforms.uGroundCentre.value.copy(this.groundCentre);
      r.autoClear = true;
      scene.overrideMaterial = this.heightMaterial;
      this.above.layers.set(GROUND_LAYER);
      r.setRenderTarget(this.ground);
      r.render(scene, this.above);
      scene.overrideMaterial = this.clearedMaterial;
      this.above.layers.set(CLEARED_LAYER);
      r.setRenderTarget(this.cleared);
      r.render(scene, this.above);
      // Car parks are plowed too: their bays, and the ground painted as one.
      r.autoClear = false;
      scene.overrideMaterial = this.baysMaterial;
      this.above.layers.set(SURFACE_LAYER);
      r.render(scene, this.above);
      scene.overrideMaterial = override;
      this.above.layers.set(GROUND_LAYER);
      for (const { mesh, landcover } of this.landcoverPieces()) {
        const own = mesh.material;
        mesh.material = this.parkingMaterial(landcover);
        r.render(mesh, this.above);
        mesh.material = own;
      }
      scene.background = background;
      scene.fog = fog;
    }

    r.autoClear = false;
    if (this.clearTracks) {
      this.clearTracks = false;
      r.setRenderTarget(this.tracks);
      r.clear(true, false, false);
    }
    if (this.shift !== undefined) {
      // Copy into the spare, offset by the move, and swap.
      this.quad.material = this.shiftMaterial;
      this.shiftMaterial.uniforms.uFrom.value = this.tracks.texture;
      this.shiftMaterial.uniforms.uOffset.value.set(this.shift.x / TRACK_SIZE, -this.shift.y / TRACK_SIZE);
      r.setRenderTarget(this.spare);
      r.render(this.quadScene, this.trackCamera);
      [this.tracks, this.spare] = [this.spare, this.tracks];
      this.uniforms.uSnowTracks.value = this.tracks.texture;
      this.shift = undefined;
    }
    this.uniforms.uTrackCentre.value.copy(this.trackCentre);
    r.setRenderTarget(this.tracks);
    // New snow, once it amounts to a step of the texture's 8 bits.
    if (this.fill >= 1 / 255) {
      const steps = Math.floor(this.fill * 255);
      this.fill -= steps / 255;
      this.quad.material = this.fillMaterial;
      this.fillMaterial.uniforms.uAmount.value = steps / 255;
      r.render(this.quadScene, this.trackCamera);
    }
    if (this.stampCount > 0) {
      this.trackCamera.position.set(this.trackCentre.x, 50, this.trackCentre.y);
      this.trackCamera.lookAt(this.trackCentre.x, 0, this.trackCentre.y);
      this.trackCamera.updateMatrixWorld();
      this.stamps.count = this.stampCount;
      this.stamps.instanceMatrix.needsUpdate = true;
      this.stamps.geometry.getAttribute('aStamp').needsUpdate = true;
      r.render(this.stampScene, this.trackCamera);
      this.stampCount = 0;
    }

    r.setRenderTarget(was);
    r.autoClear = autoClear;
    r.shadowMap.autoUpdate = shadows;
    r.setClearColor(clearColor, clearAlpha);
  }
}

const UP = new Vector3(0, 1, 0);
const stampMatrix = new Matrix4();
const stampAt = new Vector3();
const stampTurn = new Quaternion();
const stampScale = new Vector3();

function snapTo(v: number, step: number): number {
  return Math.round(v / step) * step;
}

/** A square grid ±`radius` metres, a vertex every `step`: one of the snow's two. */
function snowGrid(radius: number, step: number): BufferGeometry {
  const n = Math.round((2 * radius) / step) + 1;
  const at = (i: number) => -radius + i * step;
  const positions = new Float32Array(n * n * 3);
  const normals = new Float32Array(n * n * 3);
  for (let j = 0; j < n; j++) {
    for (let i = 0; i < n; i++) {
      const k = (j * n + i) * 3;
      positions[k] = at(i);
      positions[k + 2] = at(j);
      normals[k + 1] = 1;
    }
  }
  const index = new Uint32Array((n - 1) * (n - 1) * 6);
  let o = 0;
  for (let j = 0; j < n - 1; j++) {
    for (let i = 0; i < n - 1; i++) {
      const a = j * n + i, b = a + 1, c = a + n, d = c + 1;
      index.set([a, c, b, b, c, d], o);
      o += 6;
    }
  }
  const g = new BufferGeometry();
  g.setAttribute('position', new BufferAttribute(positions, 3));
  g.setAttribute('normal', new BufferAttribute(normals, 3));
  g.setIndex(new BufferAttribute(index, 1));
  return g;
}
